import { ALL_LOCALES, DEFAULT_LOCALE, isActiveRegion } from '@lemonade-hq/lemonation';
import { type PageName, getRouteForLocale, useI18nContext } from '@lemonade-hq/nuiza';
import Head from 'next/head';
import { useRouter } from 'next/router';
import type React from 'react';

const PINTEREST_VERIFICATION_ID = 'f215f118620fe2f473e93fd1deadb882';
const GOOGLE_VERIFICATION_ID = 'yqO8P68BvN1xtMK4gDaMmctobUf34QIx0NWDuCeP5WU';

const excludeAlternateURLS = [
  '/fr/en/sitemap',
  '/nl/en/sitemap',
  '/de/en/sitemap',
  '/giveback', // TODO: remove this when we have the giveback page translated
  '/fr/giveback', // TODO: remove this when we have the giveback page translated
  '/de/giveback', // TODO: remove this when we have the giveback page translated
  '/nl/giveback', // TODO: remove this when we have the giveback page translated
  '/uk/giveback', // TODO: remove this when we have the giveback page translated
  '/fr/en/giveback', // TODO: remove this when we have the giveback page translated
  '/de/en/giveback', // TODO: remove this when we have the giveback page translated
  '/nl/en/giveback', // TODO: remove this when we have the giveback page translated
  '/fr/en/transparency',
  '/fr/en/reviews'
];

export interface SeoProps {
  title: string;
  description: string;
  ogImage: string;
  jsonld?: Record<
    string,
    | string
    | string[]
    | StaticImageData
    | StaticImageData[]
    | Record<string, string>
    | Record<string, string | Record<string, string>>[]
  >[];
  metaTitle: string;
  noIndex?: boolean;
  advancedRobots?: boolean;
  uniqueCanonicalUrl?: string;
  excludedLocales?: string[];
}

const HOMEOWNERS_ALTERNATE_LOCALES = [
  { locale: 'en-us', url: '/homeowners' },
  { locale: 'fr-fr', url: '/fr/assurance-habitation' },
  { locale: 'en-fr', url: '/fr/en/home-insurance' },
];
const PRODUCTS = ['car', 'renters', 'homeowners', 'pet', 'life'];

const RENTERS_ALTERNATE_LOCALES = [
  { locale: 'en-us', url: '/renters' },
  { locale: 'en-fr', url: '/fr/en/renters-insurance' },
  { locale: 'fr-fr', url: '/fr/assurance-habitation-locataire' },
];

const POLICY_TWO_ALTERNATE_LOCALES = [
  { locale: 'en-us', url: '/policy-two' },
  { locale: 'fr-fr', url: '/fr/policy-2' },
  { locale: 'nl-nl', url: '/nl/polis-twee' },
  { locale: 'en-fr', url: '/fr/en/policy-two' },
  { locale: 'en-nl', url: '/nl/en/policy-two' },
];

const LEMONADE_GOES_GLOBAL_ALTERNATE_LOCALES = [
  { locale: 'en-us', url: '/lemonade-goes-global' },
  { locale: 'fr-fr', url: '/fr/lemonade-goes-global' },
  { locale: 'nl-nl', url: '/nl/lemonade-goes-global' },
  { locale: 'en-fr', url: '/fr/en/lemonade-goes-global' },
  { locale: 'en-nl', url: '/nl/en/lemonade-goes-global' },
  { locale: 'en-gb', url: '/uk/lemonade-goes-global' },
];

const FAQ_ALTERNATE_LOCALES = [
  { locale: 'en-us', url: '/faq' },
  { locale: 'fr-fr', url: '/fr/faq' },
  { locale: 'nl-nl', url: '/nl/faq' },
  { locale: 'en-fr', url: '/fr/en/faq' },
  { locale: 'en-nl', url: '/nl/en/faq' },
  { locale: 'en-gb', url: '/uk/faq' },
];
const SITEMAP_ALTERNATE_LOCALES = [
  { locale: 'en-us', url: '/sitemap' },
  { locale: 'fr-fr', url: '/fr/sitemap' },
  { locale: 'nl-nl', url: '/nl/sitemap' },
  { locale: 'en-gb', url: '/uk/sitemap' },
];

const isHomeownersPage = (path: string) => {
  return HOMEOWNERS_ALTERNATE_LOCALES.some(allowedPath => path.includes(allowedPath.url));
};

const getIsRentersPage = (path: string) => {
  return RENTERS_ALTERNATE_LOCALES.some(allowedPath => path.includes(allowedPath.url));
};

const getIsPolicyTwoPage = (path: string) => {
  return POLICY_TWO_ALTERNATE_LOCALES.some(allowedPath => path.includes(allowedPath.url));
};

const getIsLemonadeGoesGlobalPage = (path: string) => {
  return LEMONADE_GOES_GLOBAL_ALTERNATE_LOCALES.some(allowedPath => path.includes(allowedPath.url));
};

const getIsFaqPage = (path: string) => {
  return FAQ_ALTERNATE_LOCALES.some(allowedPath => path.includes(allowedPath.url));
};

const getIsSitemapPage = (path: string) => {
  return SITEMAP_ALTERNATE_LOCALES.some(allowedPath => path.includes(allowedPath.url));
};

export const SEO: React.FC<SeoProps> = ({
  title,
  description,
  ogImage,
  metaTitle,
  jsonld,
  noIndex,
  advancedRobots,
  uniqueCanonicalUrl,
  excludedLocales = ['de-DE', 'en-DE'],
}) => {
  const { activeRegions, locale } = useI18nContext();
  const router = useRouter();
  if (router.asPath.endsWith('/home')) {
    router.asPath = router.asPath.replace('/home', '');
  }
  const pageUrl = `${process.env.NEXT_PUBLIC_MONOLITH_URL}${router.asPath}`;
  const currentPath = router.pathname.substring(1);
  const isHomePage = currentPath === '' || currentPath.includes('home');
  const isGermanPage = currentPath.startsWith('de/');
  const isHomeownerPage = isHomeownersPage(`/${currentPath}`);
  const isRentersPage = getIsRentersPage(`/${currentPath}`);
  const isPolicyTwoPage = getIsPolicyTwoPage(`/${currentPath}`);
  const isLemonadeGoesGlobalPage = getIsLemonadeGoesGlobalPage(`/${currentPath}`);
  const isFaqPage = getIsFaqPage(`/${currentPath}`);
  const isSitemapPage = getIsSitemapPage(`/${currentPath}`);
  const isProductPage = PRODUCTS.some(product => currentPath.endsWith(product));
  const ogType = isHomePage || isProductPage ? 'website' : 'article';
  const currentPage = (isHomePage ? 'home' : currentPath) as PageName;
  const canonicalUrl = `${process.env.NEXT_PUBLIC_MONOLITH_URL}${router.asPath}`;
  const activeLocales = ALL_LOCALES.filter(locale => isActiveRegion(locale, activeRegions));
  const filteredLocales =
    excludedLocales != null && excludedLocales.length !== 0
      ? activeLocales.filter(value => !excludedLocales.includes(value))
      : activeLocales;

  return (
    <Head>
      <title>{metaTitle || title}</title>
      <link rel="canonical" href={uniqueCanonicalUrl || canonicalUrl} />
      
      {filteredLocales.map(locale => {
        const localizedPath = getRouteForLocale(currentPage, locale);
        if (isHomeownerPage) {
          return HOMEOWNERS_ALTERNATE_LOCALES.map(alternate => (
            <link
              key={`alternate-${alternate.locale}`}
              rel="alternate"
              href={`https://www.lemonade.com${alternate.url}`}
              hrefLang={alternate.locale.toLowerCase()}
            />
          ));
        }

        if (isFaqPage) {
          return FAQ_ALTERNATE_LOCALES.map(alternate => (
            <link
              key={`alternate-${alternate.locale}`}
              rel="alternate"
              href={`https://www.lemonade.com${alternate.url}`}
              hrefLang={alternate.locale.toLowerCase()}
            />
          ));
        }

        if (isSitemapPage) {
          return SITEMAP_ALTERNATE_LOCALES.map(alternate => (
            <link
              key={`alternate-${alternate.locale}`}
              rel="alternate"
              href={`https://www.lemonade.com${alternate.url}`}
              hrefLang={alternate.locale.toLowerCase()}
            />
          ));
        }

        if (isRentersPage) {
          return RENTERS_ALTERNATE_LOCALES.map(alternate => (
            <link
              key={`alternate-${alternate.locale}`}
              rel="alternate"
              href={`https://www.lemonade.com${alternate.url}`}
              hrefLang={alternate.locale.toLowerCase()}
            />
          ));
        }

        if (isPolicyTwoPage) {
          return POLICY_TWO_ALTERNATE_LOCALES.map(alternate => (
            <link
              key={`alternate-${alternate.locale}`}
              rel="alternate"
              href={`https://www.lemonade.com${alternate.url}`}
              hrefLang={alternate.locale.toLowerCase()}
            />
          ));
        }

        if (isLemonadeGoesGlobalPage) {
          return LEMONADE_GOES_GLOBAL_ALTERNATE_LOCALES.map(alternate => (
            <link
              key={`alternate-${alternate.locale}`}
              rel="alternate"
              href={`https://www.lemonade.com${alternate.url}`}
              hrefLang={alternate.locale.toLowerCase()}
            />
          ));
        }

        const isRootPath = currentPage.includes('home') && locale === DEFAULT_LOCALE;

        if (!localizedPath && !isRootPath) return null;

        const alternateUrl = `${process.env.NEXT_PUBLIC_MONOLITH_URL}/${
          localizedPath?.startsWith('/') ? localizedPath.substring(1) : localizedPath
        }`;

        if (localizedPath && excludeAlternateURLS.includes(localizedPath)) {
          return;
        }
        return <link key={`alternate-${locale}`} rel="alternate" href={alternateUrl} hrefLang={locale} />;
      })}
      {advancedRobots && (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}
      {(noIndex || isGermanPage) && <meta key="robots" name="robots" content="noindex" />}
      <meta name="description" content={description} />
      <meta content={process.env.FB_APP_ID} />
      <meta property="og:title" content={title} />
      {locale != null && <meta property="og:locale" content={locale.replace('-', '_')} />}
      <meta property="og:description" content={description} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:type" content={ogType} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@Lemonade_Inc" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="p:domain_verify" content={PINTEREST_VERIFICATION_ID} />
      <meta name="google-site-verification" content={GOOGLE_VERIFICATION_ID} />
      {jsonld?.map(data => (
        <script
          key={data['@type'] as string}
          type="application/ld+json" // biome-ignore lint/security/noDangerouslySetInnerHtml: we need it for formatting the JSON
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data, null, 4) }}
        />
      ))}
    </Head>
  );
};
